// FILE: src/components/Admin/AI/rag/RagPipelineEditorDialog.tsx
import React from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

type Props = {
  open: boolean;
  initialValue?: any | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => Promise<any>;
};

const defaultConfigJson = {
  token_url: 'https://id.cisco.com/oauth2/default/v1/token',
  access_token_url: 'https://chat-ai.cisco.com/ragaas-admin/api/access_token/generate',
  ingest_api_url: 'https://chat-ai.cisco.com/genai-rag/ingest/v1.0/ragaas_ingest',
  search_api_url: 'https://chat-ai.cisco.com/genai-rag/api/v2.0/ragaas',
};

const safeJsonStringify = (value: any) => {
  if (value === null || value === undefined || value === '') return '{}';

  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '{}';
  }
};

const RagPipelineEditorDialog: React.FC<Props> = ({
                                                    open,
                                                    initialValue,
                                                    saving,
                                                    onClose,
                                                    onSubmit,
                                                  }) => {
  const [form, setForm] = React.useState<any>({});
  const [defaultLocaleText, setDefaultLocaleText] = React.useState('["en_US"]');
  const [defaultAccessLevelsText, setDefaultAccessLevelsText] = React.useState('[]');
  const [defaultMetaFiltersText, setDefaultMetaFiltersText] = React.useState('{}');
  const [configJsonText, setConfigJsonText] = React.useState(JSON.stringify(defaultConfigJson, null, 2));
  const [secretValuesText, setSecretValuesText] = React.useState('{}');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;

    setForm({
      id: initialValue?.id,
      key: initialValue?.key || '',
      name: initialValue?.name || '',
      description: initialValue?.description || '',
      provider: initialValue?.provider || 'drift',
      status: initialValue?.status || 'standby',
      enabled: initialValue?.enabled ?? true,
      provider_pipeline_id: initialValue?.provider_pipeline_id || '',
      display_name: initialValue?.display_name || '',
      gcp_project: initialValue?.gcp_project || '',
      gcs_bucket: initialValue?.gcs_bucket || '',
      pubsub_topic: initialValue?.pubsub_topic || '',
      default_user_id: initialValue?.default_user_id || '',
      default_doctype: initialValue?.default_doctype || '',
      secret_name: initialValue?.secret_name || '',
      secret_region: initialValue?.secret_region || 'us-west-2',
      notes: initialValue?.notes || '',
      updated_by: initialValue?.updated_by || 'admin-ui',
    });

    setDefaultLocaleText(safeJsonStringify(initialValue?.default_locale || ['en_US']));
    setDefaultAccessLevelsText(safeJsonStringify(initialValue?.default_access_levels || []));
    setDefaultMetaFiltersText(safeJsonStringify(initialValue?.default_meta_filters || {}));
    setConfigJsonText(safeJsonStringify(initialValue?.config_json || defaultConfigJson));
    setSecretValuesText('{}');
    setError(null);
  }, [open, initialValue]);

  const setField = (key: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [key]: value }));
  };

  const parseJson = (text: string, fallback: any) => {
    if (!text.trim()) return fallback;
    return JSON.parse(text);
  };

  const submit = async () => {
    setError(null);

    if (!form.key?.trim()) {
      setError('Key is required.');
      return;
    }

    if (!form.name?.trim()) {
      setError('Name is required.');
      return;
    }

    if (form.provider === 'drift' && !form.provider_pipeline_id?.trim()) {
      setError('DRIFT Pipeline ID is required for DRIFT pipelines.');
      return;
    }

    if (form.provider === 'ragaas') {
      if (!form.gcp_project?.trim()) {
        setError('RAGaaS GCP Project is required.');
        return;
      }
      if (!form.gcs_bucket?.trim()) {
        setError('RAGaaS GCS Bucket is required.');
        return;
      }
      if (!form.pubsub_topic?.trim()) {
        setError('RAGaaS PubSub Topic is required.');
        return;
      }
    }

    try {
      const secretValues = parseJson(secretValuesText, {});
      const payload: any = {
        ...form,
        default_locale: parseJson(defaultLocaleText, []),
        default_access_levels: parseJson(defaultAccessLevelsText, []),
        default_meta_filters: parseJson(defaultMetaFiltersText, {}),
        config_json: parseJson(configJsonText, {}),
      };

      if (secretValues && typeof secretValues === 'object' && !Array.isArray(secretValues) && Object.keys(secretValues).length) {
        payload.secret_values = secretValues;
      }

      await onSubmit(payload);
    } catch (err: any) {
      setError(err?.message || 'Save failed.');
    }
  };

  const provider = form.provider || 'drift';

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>{form.id ? 'Edit RAG Pipeline' : 'Add RAG Pipeline'}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Key"
                value={form.key || ''}
                onChange={event => setField('key', event.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Name"
                value={form.name || ''}
                onChange={event => setField('name', event.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Provider"
                value={provider}
                onChange={event => setField('provider', event.target.value)}
              >
                <MenuItem value="drift">DRIFT</MenuItem>
                <MenuItem value="ragaas">RAGaaS</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Status"
                value={form.status || 'standby'}
                onChange={event => setField('status', event.target.value)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="standby">Standby</MenuItem>
                <MenuItem value="ingesting">Ingesting</MenuItem>
                <MenuItem value="validating">Validating</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.enabled)}
                    onChange={event => setField('enabled', event.target.checked)}
                  />
                }
                label="Enabled"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Display Name"
                value={form.display_name || ''}
                onChange={event => setField('display_name', event.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Description"
                value={form.description || ''}
                onChange={event => setField('description', event.target.value)}
              />
            </Grid>

            {provider === 'drift' || provider === 'other' ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required={provider === 'drift'}
                  label="Provider Pipeline ID"
                  value={form.provider_pipeline_id || ''}
                  onChange={event => setField('provider_pipeline_id', event.target.value)}
                  helperText={provider === 'drift' ? 'DRIFT pipeline_id.' : undefined}
                />
              </Grid>
            ) : null}

            {provider === 'ragaas' ? (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="GCP Project"
                    value={form.gcp_project || ''}
                    onChange={event => setField('gcp_project', event.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="GCS Bucket"
                    value={form.gcs_bucket || ''}
                    onChange={event => setField('gcs_bucket', event.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="PubSub Topic"
                    value={form.pubsub_topic || ''}
                    onChange={event => setField('pubsub_topic', event.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Default User ID"
                    value={form.default_user_id || ''}
                    onChange={event => setField('default_user_id', event.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Default Doctype"
                    value={form.default_doctype || ''}
                    onChange={event => setField('default_doctype', event.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="AWS Secret Region"
                    value={form.secret_region || ''}
                    onChange={event => setField('secret_region', event.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Default Locale JSON"
                    value={defaultLocaleText}
                    onChange={event => setDefaultLocaleText(event.target.value)}
                    helperText='Example: ["en_US"]'
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Default Access Levels JSON"
                    value={defaultAccessLevelsText}
                    onChange={event => setDefaultAccessLevelsText(event.target.value)}
                    helperText='Example: ["Employee"]'
                  />
                </Grid>
              </>
            ) : null}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                label="Default Meta Filters JSON"
                value={defaultMetaFiltersText}
                onChange={event => setDefaultMetaFiltersText(event.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={8}
                label="Provider Config JSON"
                value={configJsonText}
                onChange={event => setConfigJsonText(event.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="AWS Secret Name"
                value={form.secret_name || ''}
                onChange={event => setField('secret_name', event.target.value)}
                helperText="Optional. If omitted and secret values are provided, the backend generates a name."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                label="Secret Values JSON"
                value={secretValuesText}
                onChange={event => setSecretValuesText(event.target.value)}
                helperText="Write-only. Leave {} to preserve existing secret. RAGaaS keys: ingest_api_key, search_api_key, ingest_client_secret, search_client_secret."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Notes"
                value={form.notes || ''}
                onChange={event => setField('notes', event.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Updated By"
                value={form.updated_by || ''}
                onChange={event => setField('updated_by', event.target.value)}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>

        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RagPipelineEditorDialog;